import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../lib/auth";
import { Loader2 } from "lucide-react";
import { track, identify, EVENTS } from "../lib/analytics";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      const to = user.onboarding_complete ? "/dashboard" : "/onboarding";
      navigate(to, { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      identify(u.id, { email: u.email, plan: u.plan, role: u.role });
      track(EVENTS.LOGIN_COMPLETED, { plan: u.plan, role: u.role });
      if (email.toLowerCase() === "demo@cherishlink.com") {
        track(EVENTS.DEMO_LOGIN_USED);
      }
      const from = location.state?.from?.pathname;
      const to = from && !["/login", "/signup"].includes(from) ? from : (u.onboarding_complete ? "/dashboard" : "/onboarding");
      navigate(to, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("demo@cherishlink.com");
    setPassword("password123");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 cherishlink-gradient-bg">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(212 73% 96%) 0%, hsl(177 45% 96%) 100%)" }}>
        <Logo to="/" size="lg" />
        <div className="relative z-10">
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight text-foreground">
            Peace of mind,<br />one warm call at a time.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-md">
            Families across the country stay close to aging loved ones with CherishLink.
            Sign in to check in on yours.
          </p>
        </div>
        <div className="text-xs text-muted-foreground/70">© {new Date().getFullYear()} CherishLink</div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-8 md:p-10">
            <div className="lg:hidden mb-8"><Logo /></div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1>
            <p className="mt-2 text-muted-foreground">Sign in to your CherishLink account.</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" required
                  data-testid="login-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11 rounded-xl"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="login-forgot-link">
                    Forgot?
                  </Link>
                </div>
                <Input
                  id="password" type="password" required
                  data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-11 rounded-xl"
                  placeholder="••••••••"
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full" data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>

            <button
              type="button"
              onClick={fillDemo}
              className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              data-testid="login-demo-fill"
            >
              Try the demo: demo@cherishlink.com / password123
            </button>

            <p className="mt-8 text-sm text-center text-muted-foreground">
              New to CherishLink?{" "}
              <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="login-to-signup">
                Create an account
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
